import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Conversation, Message, SendMessageInput } from '@neuroedge/shared';
import { Avatar, Button, Card, PageState, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import {
  useConversations,
  useConversation,
  useMessages,
  useSendMessage,
  useSendImageMessage,
  useMarkConversationRead,
  usePatchConversation,
  useDownloadMessageMedia,
} from '../../api/conversations';

const UPLOADABLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const CONVERSATION_STATUSES = ['OPEN', 'PENDING', 'SNOOZED', 'CLOSED'] as const;
import { useWhatsappTemplates } from '../../api/whatsapp-templates';

type InboxFilter = 'open' | 'unread' | 'all';
const INBOX_FILTERS: { value: InboxFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'unread', label: 'Unread' },
  { value: 'all', label: 'All' },
];

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

function ConversationRow({
  conversation,
  active,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={active ? 'ne-inbox__row ne-inbox__row--active' : 'ne-inbox__row'}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: 'var(--space-3)',
        border: 'none',
        borderBottom: '1px solid var(--color-border)',
        background: active ? 'var(--color-surface-raised, #eef)' : 'transparent',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Avatar name={conversation.contact.displayName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
            <strong>{conversation.contact.displayName}</strong>
            {conversation.unreadCount > 0 ? (
              <span className="ne-unread-badge">{conversation.unreadCount}</span>
            ) : null}
          </div>
          {conversation.contact.phone ? (
            <div
              style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs, 11px)' }}
            >
              ({conversation.contact.phone})
            </div>
          ) : null}
          <div
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {conversation.lastMessagePreview ?? 'No messages yet'}
          </div>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs, 11px)' }}>
            {conversation.channel.name} · {when(conversation.lastMessageAt)}
          </div>
        </div>
      </div>
    </button>
  );
}

function MessageMedia({
  conversationId,
  messageId,
  media,
  messageType,
}: {
  conversationId: string;
  messageId: string;
  media: NonNullable<Message['media']>;
  messageType: Message['messageType'];
}) {
  const download = useDownloadMessageMedia(conversationId);
  const toast = useToast();

  async function onClick() {
    try {
      await download.mutateAsync(messageId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download the attachment.');
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        fontSize: 'var(--font-size-sm)',
        opacity: 0.9,
        marginBottom: 'var(--space-1)',
      }}
    >
      <span>
        📎 {media.fileName ?? messageType.toLowerCase()}
        {media.mimeType ? ` · ${media.mimeType}` : ''}
      </span>
      <button
        type="button"
        onClick={() => void onClick()}
        disabled={download.isPending}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          color: 'inherit',
          textDecoration: 'underline',
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        {download.isPending ? 'Downloading…' : 'Download'}
      </button>
    </div>
  );
}

function MessageBubble({ message, conversationId }: { message: Message; conversationId: string }) {
  const outbound = message.direction === 'OUTBOUND';
  return (
    <div style={{ display: 'flex', justifyContent: outbound ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '70%',
          padding: 'var(--space-2) var(--space-3)',
          borderRadius: 'var(--radius-md, 8px)',
          background: outbound
            ? 'var(--color-primary, #2563eb)'
            : 'var(--color-surface-raised, #eee)',
          color: outbound ? '#fff' : 'inherit',
        }}
      >
        {message.media ? (
          <MessageMedia
            conversationId={conversationId}
            messageId={message.id}
            media={message.media}
            messageType={message.messageType}
          />
        ) : message.imageUrl ? (
          <img
            src={message.imageUrl}
            alt=""
            style={{ maxWidth: '100%', borderRadius: 'var(--radius-md, 8px)', display: 'block' }}
          />
        ) : null}
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {message.content ?? `[${message.messageType}]`}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs, 11px)', opacity: 0.8, marginTop: 2 }}>
          {message.direction === 'OUTBOUND' ? message.status : when(message.createdAt)}
          {message.status === 'FAILED' && message.errorMessage ? ` · ${message.errorMessage}` : ''}
        </div>
      </div>
    </div>
  );
}

function Composer({
  channelId,
  channelType,
  pending,
  onSend,
  onSendImage,
}: {
  channelId: string;
  channelType: string;
  pending: boolean;
  onSend: (input: SendMessageInput) => Promise<void>;
  onSendImage: (file: File, caption: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<'text' | 'template' | 'image'>('text');
  const [draft, setDraft] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [vars, setVars] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const isWhatsapp = channelType === 'WHATSAPP';
  const templates = useWhatsappTemplates(
    { channelId, status: 'APPROVED', pageSize: 100 },
    isWhatsapp && mode === 'template',
  );
  const selected = useMemo(
    () => templates.data?.items.find((t) => t.id === templateId),
    [templates.data, templateId],
  );

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === 'template') {
        if (!selected) return;
        for (let i = 1; i <= selected.bodyVariableCount; i += 1) {
          if (!vars[String(i)]?.trim()) {
            setError(`Fill in variable {{${i}}}.`);
            return;
          }
        }
        await onSend({ messageType: 'TEMPLATE', templateId: selected.id, variables: vars });
        setTemplateId('');
        setVars({});
      } else if (mode === 'image') {
        if (!imageFile) return;
        await onSendImage(imageFile, caption);
        setImageFile(null);
        setCaption('');
      } else {
        if (!draft.trim()) return;
        await onSend({ content: draft.trim() });
        setDraft('');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not send the message.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <form
      onSubmit={submit}
      style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}
    >
      {isWhatsapp ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Button
            type="button"
            variant={mode === 'text' ? 'primary' : 'secondary'}
            onClick={() => setMode('text')}
          >
            Text
          </Button>
          <Button
            type="button"
            variant={mode === 'template' ? 'primary' : 'secondary'}
            onClick={() => setMode('template')}
          >
            Template
          </Button>
          <Button
            type="button"
            variant={mode === 'image' ? 'primary' : 'secondary'}
            onClick={() => setMode('image')}
          >
            Image
          </Button>
        </div>
      ) : null}

      {mode === 'template' && isWhatsapp ? (
        <>
          <select
            className="ne-input"
            aria-label="Template"
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setVars({});
            }}
          >
            <option value="">Select a template…</option>
            {(templates.data?.items ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.language})
              </option>
            ))}
          </select>
          {selected
            ? Array.from({ length: selected.bodyVariableCount }, (_, i) => String(i + 1)).map(
                (n) => (
                  <input
                    key={n}
                    className="ne-input"
                    placeholder={`Variable {{${n}}}`}
                    aria-label={`Variable ${n}`}
                    value={vars[n] ?? ''}
                    onChange={(e) => setVars((v) => ({ ...v, [n]: e.target.value }))}
                  />
                ),
              )
            : null}
        </>
      ) : mode === 'image' && isWhatsapp ? (
        <>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="Image to attach"
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              setError(null);
              if (file && !UPLOADABLE_IMAGE_TYPES.has(file.type)) {
                setError('Only JPEG, PNG, or WEBP images are supported.');
                setImageFile(null);
              } else if (file && file.size > MAX_IMAGE_BYTES) {
                setError('Image must be 5MB or smaller.');
                setImageFile(null);
              } else {
                setImageFile(file);
              }
            }}
          />
          {imageFile ? (
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
              {imageFile.name} · {(imageFile.size / 1024).toFixed(0)} KB
            </span>
          ) : null}
          <textarea
            className="ne-input"
            rows={2}
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            aria-label="Caption"
          />
        </>
      ) : (
        <textarea
          className="ne-input"
          rows={3}
          placeholder="Type a reply…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="Reply"
        />
      )}

      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      <div>
        <Button
          type="submit"
          disabled={
            pending ||
            (mode === 'text' ? !draft.trim() : mode === 'image' ? !imageFile : !templateId)
          }
        >
          {pending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </form>
  );
}

function Thread({
  conversationId,
  canReply,
  canManage,
}: {
  conversationId: string;
  canReply: boolean;
  canManage: boolean;
}) {
  const detail = useConversation(conversationId);
  const messages = useMessages(conversationId);
  const send = useSendMessage(conversationId);
  const sendImage = useSendImageMessage(conversationId);
  const markRead = useMarkConversationRead();
  const patch = usePatchConversation(conversationId);
  const toast = useToast();
  const markReadMutate = markRead.mutate;
  const endRef = useRef<HTMLDivElement>(null);

  const unread = detail.data?.unreadCount ?? 0;
  useEffect(() => {
    if (unread > 0) markReadMutate(conversationId);
  }, [conversationId, unread, markReadMutate]);

  const messageCount = messages.data?.items.length ?? 0;
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messageCount]);

  if (detail.isPending) return <PageState kind="loading" message="Loading conversation…" />;
  if (detail.isError)
    return (
      <PageState
        kind="error"
        message={detail.error.message}
        onRetry={() => void detail.refetch()}
      />
    );

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Avatar name={detail.data.contact.displayName} />
          <div>
            <div>
              {detail.data.contact.displayName} · {detail.data.channel.name}
            </div>
            {detail.data.contact.phone ? (
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 400,
                  color: 'var(--color-text-muted)',
                }}
              >
                ({detail.data.contact.phone})
              </div>
            ) : null}
          </div>
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
        }}
      >
        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {detail.data.assignedUser
            ? `Assigned to ${detail.data.assignedUser.displayName}`
            : 'Unassigned'}
        </span>
        {canManage ? (
          <select
            className="ne-input"
            aria-label="Conversation status"
            style={{ width: 'auto' }}
            value={detail.data.status}
            disabled={patch.isPending}
            onChange={(e) =>
              patch.mutate(
                { status: e.target.value as (typeof CONVERSATION_STATUSES)[number] },
                {
                  onSuccess: () => toast.success('Conversation status updated.'),
                  onError: (err) =>
                    toast.error(
                      err instanceof Error ? err.message : 'Could not update the conversation.',
                    ),
                },
              )
            }
          >
            {CONVERSATION_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        ) : (
          <span className="ne-badge">{detail.data.status}</span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
          maxHeight: 420,
          overflowY: 'auto',
          padding: 'var(--space-1)',
        }}
      >
        {messages.isPending ? (
          <PageState kind="loading" message="Loading messages…" />
        ) : messages.isError ? (
          <PageState kind="error" message={messages.error.message} />
        ) : messages.data.items.length === 0 ? (
          <PageState kind="empty" title="No messages yet" />
        ) : (
          messages.data.items.map((m) => (
            <MessageBubble key={m.id} message={m} conversationId={conversationId} />
          ))
        )}
        <div ref={endRef} />
      </div>

      {canReply ? (
        <Composer
          channelId={detail.data.channel.id}
          channelType={detail.data.channel.type}
          pending={send.isPending || sendImage.isPending}
          onSend={(input) => send.mutateAsync(input).then(() => undefined)}
          onSendImage={(file, caption) =>
            sendImage.mutateAsync({ file, caption }).then(() => undefined)
          }
        />
      ) : (
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-text-muted)' }}>
          You do not have permission to reply in conversations.
        </p>
      )}
    </Card>
  );
}

export function InboxPage() {
  const { hasPermission } = useAuth();
  const canReply = hasPermission('conversations.reply');
  const canManage = hasPermission('conversations.assign');
  // `?conversationId=` is the command palette's deep link (jump straight to
  // a specific conversation found by its contact's name) - read once on
  // mount as the initial selection, same as any other "open on this one"
  // entry point; the user can still pick a different row afterward.
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<string | undefined>(
    () => searchParams.get('conversationId') ?? undefined,
  );
  // `?filter=` is the dashboard's "Open conversations"/"Unread messages"
  // tile deep link - read once on mount, same as conversationId above; the
  // tabs below still let the user switch afterward.
  const [filter, setFilter] = useState<InboxFilter>(() => {
    const requested = searchParams.get('filter');
    return requested === 'open' || requested === 'unread' || requested === 'all'
      ? requested
      : 'all';
  });
  const conversations = useConversations({
    sortBy: 'lastMessageAt',
    sortOrder: 'desc',
    ...(filter === 'open' ? { status: 'OPEN' } : {}),
    ...(filter === 'unread' ? { unreadOnly: true } : {}),
  });
  // Always the *total* unread count regardless of the active tab - same
  // metric the sidebar's Inbox badge and the dashboard tile use.
  const unreadConversations = useConversations({ unreadOnly: true, pageSize: 1 });
  const totalUnread = unreadConversations.data?.meta.total ?? 0;

  const items = conversations.data?.items ?? [];
  const active = selected ?? items[0]?.id;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Inbox</h1>
        {totalUnread > 0 ? <span className="ne-unread-badge">{totalUnread}</span> : null}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {INBOX_FILTERS.map((tab) => (
          <Button
            key={tab.value}
            type="button"
            variant={filter === tab.value ? 'primary' : 'secondary'}
            onClick={() => setFilter(tab.value)}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {conversations.isPending ? (
        <PageState kind="loading" message="Loading conversations…" />
      ) : conversations.isError ? (
        <PageState
          kind="error"
          message={conversations.error.message}
          onRetry={() => void conversations.refetch()}
        />
      ) : items.length === 0 ? (
        <PageState
          kind="empty"
          title="No conversations yet"
          message="Inbound WhatsApp messages will appear here."
        />
      ) : (
        <div className="inbox-grid">
          <Card title="Conversations">
            <div style={{ margin: '0 calc(-1 * var(--space-3))' }}>
              {items.map((c) => (
                <ConversationRow
                  key={c.id}
                  conversation={c}
                  active={c.id === active}
                  onSelect={() => setSelected(c.id)}
                />
              ))}
            </div>
          </Card>
          {active ? (
            <Thread conversationId={active} canReply={canReply} canManage={canManage} />
          ) : null}
        </div>
      )}

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
